import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Calculate-mode panel for the resin Border. Captures length (m) + width (mm),
 * displays the calculated m² + the suggested mixes/trim lengths.
 *
 * Suggestion values are computed by the parent via the existing `useEffect`
 * in `Border.js` and passed in as `suggestedMixes` / `suggestedRigidTrim`.
 */
export default function BorderCalculateInputs({
  length,
  setLength,
  width,
  setWidth,
  suggestedMixes,
  suggestedRigidTrim,
}) {
  return (
    <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-white rounded-xl border border-gray-200">
      <div>
        <Label htmlFor="border-length" className="text-sm font-medium text-gray-700 mb-2 block">
          Border Length (m)
        </Label>
        <Input
          id="border-length"
          type="number"
          step="0.01"
          min="0"
          placeholder="e.g. 10"
          value={length}
          onChange={(e) => setLength(e.target.value)}
          className="h-12 text-lg border-2 focus:border-[#300122] rounded-xl"
          data-testid="border-length-input"
        />
      </div>
      <div>
        <Label htmlFor="border-width" className="text-sm font-medium text-gray-700 mb-2 block">
          Border Width (mm)
        </Label>
        <Input
          id="border-width"
          type="number"
          step="1"
          min="0"
          placeholder="e.g. 150"
          value={width}
          onChange={(e) => setWidth(e.target.value)}
          className="h-12 text-lg border-2 focus:border-[#300122] rounded-xl"
          data-testid="border-width-input"
        />
      </div>
      {length && width && (
        <div className="col-span-2 p-3 bg-[#300122]/10 rounded-lg text-sm">
          <div className="flex justify-between mb-1">
            <span className="text-gray-600">Calculated area:</span>
            <span className="font-bold text-[#300122]">
              {((parseFloat(length) || 0) * (parseFloat(width) || 0) / 1000).toFixed(3)} m²
            </span>
          </div>
        </div>
      )}
      {length && (
        <div className="col-span-2 p-4 bg-[#2F5E96]/10 rounded-lg border-2 border-[#2F5E96]/30">
          <p className="text-sm font-medium text-[#2F5E96] mb-2">Based on your selection, you will need:</p>
          <div className="space-y-1 text-sm">
            {width && (
              <div className="flex justify-between">
                <span className="text-gray-600">Suggested mixes:</span>
                <span className="font-bold text-[#2F5E96]">{suggestedMixes} mixes</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Suggested trim lengths:</span>
              <span className="font-bold text-[#2F5E96]">{suggestedRigidTrim} lengths</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 italic mt-2">
            These values have been added below. You can adjust the quantities if needed.
          </p>
        </div>
      )}
    </div>
  );
}
